import { graphql } from '@predium/client-graphql';

//Economic unit ------------------------------------

export const ADD_ECONOMIC_UNIT = graphql(`
  mutation DataCollectionAddEconomicUnit(
    $portfolioId: Int!
    $name: String!
    $customerEconomicUnitId: String!
    $description: String
    $responsibleUserId: Int
  ) {
    insert_economic_unit_one(
      object: {
        name: $name
        portfolio_id: $portfolioId
        customer_economic_unit_id: $customerEconomicUnitId
        description: $description
        responsible_user_id: $responsibleUserId
      }
    ) {
      __typename
      id
    }
  }
`);

export const EDIT_ECONOMIC_UNIT = graphql(`
  mutation DataCollectionEditEconomicUnit(
    $economicUnitId: Int!
    $economicUnitName: String!
    $customerEconomicUnitId: String
    $description: String
    $portfolioId: Int!
    $responsibleUserId: Int
    $customData: JSON!
    $includeCustomData: Boolean!
  ) {
    update_economic_unit_by_pk(
      pk_columns: { id: $economicUnitId }
      _set: {
        name: $economicUnitName
        customer_economic_unit_id: $customerEconomicUnitId
        description: $description
        portfolio_id: $portfolioId
        responsible_user_id: $responsibleUserId
      }
    ) {
      id
      name
      portfolio_id
      responsible_user_id
      customer_economic_unit_id
      description
      responsible_user {
        id
        first_name
        last_name
      }
    }
    # custom_data is only updatable through this validated action (type/key/length checks + JSONB merge);
    # included here so a single request saves both the entity fields and the custom-property values.
    updateEconomicUnitCustomData(economicUnitId: $economicUnitId, customData: $customData)
      @include(if: $includeCustomData) {
      success
      errorMessage
    }
  }
`);

export const DELETE_ECONOMIC_UNIT = graphql(`
  mutation DataCollectionDeleteEconomicUnit($id: Int!) {
    delete_economic_unit_by_pk(id: $id) {
      __typename
      id
      buildings {
        id
      }
    }
  }
`);

export const DELETE_ECONOMIC_UNITS = graphql(`
  mutation DataCollectionDeleteEconomicUnits($where: economic_unit_bool_exp!) {
    delete_economic_unit(where: $where) {
      affected_rows
      returning {
        __typename
        id
      }
    }
  }
`);

//Building ------------------------------------

export const DELETE_BUILDING = graphql(`
  mutation DataCollectionDeleteBuilding($id: Int!) {
    delete_building_by_pk(id: $id) {
      id
      economic_unit {
        id
        associatedBuildingsAndDrafts {
          amount
        }
      }
    }
  }
`);

export const DELETE_INACTIVE_BUILDINGS_BY_ID = graphql(`
  mutation DataCollectionDeleteInactiveBuildingsById($buildingIds: [Int!]!) {
    delete_inactive_buildings(where: { id: { _in: $buildingIds } }) {
      affected_rows
      returning {
        id
        economic_unit {
          id
          associatedBuildingsAndDrafts {
            amount
          }
        }
      }
    }
  }
`);

export const UPDATE_BUILDING_LOCATION = graphql(`
  mutation UpdateBuildingLocation(
    $buildingId: Float!
    $coordinates: Coordinates
    $selectedIds: [String!]!
    $approximate: Boolean!
    $buildingCitydbGmlIds: [String!]
    $neighborCitydbGmlIds: [String!]
    $lod1BuildingIds: [String!]
    $neighborLod1Ids: [String!]
  ) {
    updateBuildingLocation(
      buildingId: $buildingId
      coordinates: $coordinates
      selectedIds: $selectedIds
      approximate: $approximate
      buildingCitydbGmlIds: $buildingCitydbGmlIds
      neighborCitydbGmlIds: $neighborCitydbGmlIds
      lod1BuildingIds: $lod1BuildingIds
      neighborLod1Ids: $neighborLod1Ids
    ) {
      message
      success
      building {
        id
        address {
          id
          mapbox_building_ids
          building_citydb_gml_ids
          building_lod1_ids
          latitude
          longitude
        }
      }
    }
  }
`);

export const DELETE_BUILDING_DRAFTS = graphql(`
  mutation DataCollectionBuildingDraftsListDeleteBuildingDrafts($where: building_draft_bool_exp!) {
    delete_building_draft(where: $where) {
      affected_rows
    }
  }
`);

export const UPLOAD_ENERGY_CERTIFICATE_DRAFT = graphql(`
  mutation UploadEnergyCertificateDraft($buildingDraftId: Int!, $fileName: String!) {
    uploadEnergyCertificateDraft(buildingDraftId: $buildingDraftId, fileName: $fileName) {
      uploadUrl
      energyCertificateDraftId
      UploadEnergyCertificateDraftId {
        ...EPCDraftWithFileURLFragment
      }
    }
  }
`);

export const DELETE_ENERGY_CERTIFICATE_DRAFT = graphql(`
  mutation DeleteEnergyCertificateDraft($id: Int!) {
    delete_energy_certificate_draft_by_pk(id: $id) {
      id
    }
  }
`);

export const DELETE_ENERGY_CERTIFICATE = graphql(`
  mutation DeleteEnergyCertificate($id: Int!) {
    delete_energy_certificate_by_pk(id: $id) {
      id
    }
  }
`);

// Consumption draft ------------------------------------

export const UPDATE_CONSUMPTION_INVOICE_DRAFT = graphql(`
  mutation DataCollectionSaveConsumptionInvoiceDraft(
    $buildingId: Int!
    $consumptionInvoiceDraftId: Int!
    $invoicePayer: invoice_payer_type_enum
    $invoiceDate: DateTime
    $draftStatus: draft_status_enum!
    $consumptionDrafts: [ConsumptionDraftInput!]!
  ) {
    updateConsumptionInvoiceDraft(
      buildingId: $buildingId
      invoiceDraftId: $consumptionInvoiceDraftId
      invoiceDate: $invoiceDate
      invoicePayer: $invoicePayer
      draftStatus: $draftStatus
      consumptionDrafts: $consumptionDrafts
    ) {
      UpdateConsumptionInvoiceDraftOutput {
        id
        date
        invoice_payer
        building {
          id
        }
        draft_status
        consumption_drafts {
          id
          from
          to
          provider

          value
          display_unit_value
          cost
          consumption_type_id
          energy_source_type_id
          sub_type_id
          consumption_waste_details_draft {
            id
            volume
            display_unit_volume
            frequency
            display_unit_frequency
            amount
            price
          }
        }
      }
    }
  }
`);

export const CREATE_CONSUMPTION_INVOICE_FROM_DRAFT = graphql(`
  mutation DataCollectionAreaConsumptionDraftCreateConsumptionFromDraft($consumptionInvoiceDraftId: Int!) {
    createConsumptionInvoiceFromDraft(consumption_invoice_draft_id: $consumptionInvoiceDraftId) {
      consumption_invoice_id
    }
  }
`);

export const CONSUMPTION_DRAFT_STATUS_SUBSCRIPTION = graphql(`
  subscription DataCollectionConsumptionDraftStatusSubscription($consumptionInvoiceDraftId: Int!) {
    consumption_invoice_draft_by_pk(id: $consumptionInvoiceDraftId) {
      id
      draft_status
      updated_at
    }
  }
`);

export const SET_CONSUMPTION_INVOICE_DRAFT_OCR_FAILED = graphql(`
  mutation DataCollectionAreaConsumptionInvoiceDraftSetOcrFailed($consumptionInvoiceDraftId: Int!) {
    update_consumption_invoice_draft_by_pk(
      pk_columns: { id: $consumptionInvoiceDraftId }
      _set: { draft_status: OCR_FAILED }
    ) {
      id
    }
  }
`);

export const CREATE_CONSUMPTION_INVOICE_DRAFT = graphql(`
  mutation UploadConsumptionDraftModalCreateConsumptionDraft($buildingId: Int!) {
    createConsumptionInvoiceDraft(building_id: $buildingId) {
      CreateConsumptionInvoiceDraftOutput {
        id
        file_id
        created_at
      }
      upload_url
    }
  }
`);

//Energy certificate -----------------------------------

//Building model ------------------------------------

export const APPLY_BUILDING_MODEL_MUTATIONS = graphql(`
  mutation ApplyBuildingModelMutations(
    $buildingModelId: Int!
    $buildingModelMutation: BuildingModelMutation!
    $envelopeMutations: [EnvelopeMutation!]!
    $energySystemMutations: [EnergySystemMutation!]!
    $energySystemConsumerMutations: [EnergyConsumerMutation!]!
    $energyProducerMutations: [EnergyProducerMutation!]!
    $energySystemStorageMutations: [EnergyStorageMutation!]!
    $energySystemDistributionMutations: [EnergyDistributionMutation!]!
    $saveInvalidState: Boolean!
    $dryRun: Boolean! = false
  ) {
    applyBuildingModelMutations(
      building_model_id: $buildingModelId
      building_model_mutation: $buildingModelMutation
      envelope_mutations: $envelopeMutations
      energy_system_mutations: $energySystemMutations
      energy_consumer_mutations: $energySystemConsumerMutations
      energy_producer_mutations: $energyProducerMutations
      energy_storage_mutations: $energySystemStorageMutations
      energy_distribution_mutations: $energySystemDistributionMutations
      save_invalid_state: $saveInvalidState
      dry_run: $dryRun
    ) {
      scaling_successful
      final_energy
      final_energy_scaled
      heating_demand_envelope
      heating_demand_system
    }
  }
`);

export const UPDATE_CONSUMPTION_INVOICE = graphql(`
  mutation DataCollectionSaveConsumptionInvoice(
    $buildingId: Int!
    $consumptionInvoiceId: Int!
    $invoicePayer: invoice_payer_type_enum!
    $invoiceDate: DateTime!
    $consumptions: [ConsumptionInput!]!
  ) {
    updateConsumptionInvoice(
      buildingId: $buildingId
      invoiceId: $consumptionInvoiceId
      invoiceDate: $invoiceDate
      invoicePayer: $invoicePayer
      consumptions: $consumptions
    ) {
      UpdateConsumptionInvoiceOutput {
        id
        date
        invoice_payer
        building {
          id
        }
        consumptions {
          id
          # shared
          from
          to
          provider

          value
          display_unit_value
          cost
          consumption_type_id
          energy_source_type_id
          sub_type_id
          consumption_waste_detail {
            id
            volume
            display_unit_volume
            frequency
            display_unit_frequency
            amount
            price
          }
        }
      }
    }
  }
`);

export const INSERT_SINGLE_CONSUMPTION_DRAFT = graphql(`
  mutation DataCollectionInsertConsumptionDraft(
    $buildingId: Int!
    $areaIds: [Int!]
    $from: DateTime
    $to: DateTime
    $value: Float
    $displayUnitValue: consumption_unit_enum
    $cost: Float
    $provider: String
    $sourceTypeId: consumption_source_type_enum!
    $consumptionTypeId: consumption_type_enum
    $energySourceTypeId: energy_source_type_enum
    $subTypeId: consumption_sub_type_enum
    $userId: Int!
  ) {
    createConsumptionDraft(
      buildingId: $buildingId
      areaIds: $areaIds
      from: $from
      to: $to
      value: $value
      displayUnitValue: $displayUnitValue
      cost: $cost
      provider: $provider
      sourceTypeId: $sourceTypeId
      consumptionTypeId: $consumptionTypeId
      energySourceTypeId: $energySourceTypeId
      subTypeId: $subTypeId
      userId: $userId
    ) {
      id
      consumption_draft {
        id
        building_id
        consumption_type_id
        cost
        created_by_user_id
        energy_source_type_id
        from
        provider
        source_type_id
        sub_type_id
        to
        displayValue
        display_unit_value
      }
    }
  }
`);

export const INSERT_SINGLE_WASTE_DETAILS_DRAFT = graphql(`
  mutation DataCollectionInsertConsumptionWasteDetailsDraft(
    $consumptionDraftId: Int!
    $amount: Float
    $frequency: Float
    $displayUnitFrequency: consumption_frequency_type_enum
    $price: Float
    $volume: Float
    $displayUnitVolume: consumption_unit_enum
  ) {
    createConsumptionWasteDetailsDraft(
      consumptionDraftId: $consumptionDraftId
      amount: $amount
      frequency: $frequency
      displayUnitFrequency: $displayUnitFrequency
      price: $price
      volume: $volume
      displayUnitVolume: $displayUnitVolume
    ) {
      id
      consumption_waste_details_draft {
        id
        amount
        frequency
        display_unit_frequency
        price
        volume
        display_unit_volume
      }
    }
  }
`);

export const UPDATE_SINGLE_CONSUMPTION_DRAFT = graphql(`
  mutation DataCollectionSaveConsumptionDraft(
    $consumptionDraftId: Int!
    $areaIds: [Int!]
    $from: DateTime
    $to: DateTime
    $value: Float
    $displayUnitValue: consumption_unit_enum
    $cost: Float
    $provider: String
    $consumptionTypeId: consumption_type_enum
    $energySourceTypeId: energy_source_type_enum
    $subTypeId: consumption_sub_type_enum
  ) {
    updateConsumptionDraft(
      consumptionDraftId: $consumptionDraftId
      areaIds: $areaIds
      from: $from
      to: $to
      value: $value
      displayUnitValue: $displayUnitValue
      cost: $cost
      provider: $provider
      consumptionTypeId: $consumptionTypeId
      energySourceTypeId: $energySourceTypeId
      subTypeId: $subTypeId
    ) {
      id
      consumption_draft {
        id
        building_id
        consumption_type_id
        cost
        created_by_user_id
        energy_source_type_id
        from
        provider
        source_type_id
        sub_type_id
        to
        value
        displayValue
        display_unit_value
        consumption_waste_details_draft {
          id
        }
      }
    }
  }
`);

export const UPDATE_SINGLE_WASTE_DETAILS_DRAFT = graphql(`
  mutation DataCollectionUpdateConsumptionWasteDetailsDraft(
    $id: Int!
    $amount: Float
    $frequency: Float
    $displayUnitFrequency: consumption_frequency_type_enum
    $price: Float
    $volume: Float
    $displayUnitVolume: consumption_unit_enum
  ) {
    updateConsumptionWasteDetailsDraft(
      id: $id
      amount: $amount
      frequency: $frequency
      displayUnitFrequency: $displayUnitFrequency
      price: $price
      volume: $volume
      displayUnitVolume: $displayUnitVolume
    ) {
      id
      consumption_waste_details_draft {
        id
        amount
        frequency
        display_unit_frequency
        price
        volume
        display_unit_volume
      }
    }
  }
`);

export const DELETE_SINGLE_WASTE_DETAILS_DRAFT = graphql(`
  mutation DataCollectionDeleteConsumptionWasteDetailsDraft($id: Int!) {
    delete_consumption_waste_details_draft_by_pk(id: $id) {
      id
    }
  }
`);
export const CREATE_MANUAL_CONSUMPTION = graphql(`
  mutation DataCollectionCreateManualConsumption(
    $areaIds: [Int!]!
    $buildingId: Int!
    $consumptionTypeId: consumption_type_enum!
    $cost: Float
    $displayUnitValue: consumption_unit_enum
    $energySourceTypeId: energy_source_type_enum
    $from: DateTime!
    $provider: String!
    $sourceTypeId: ManualConsumptionSourceTypeEnum!
    $subTypeId: consumption_sub_type_enum
    $to: DateTime!
    $value: Float
    $wasteDetails: WasteDetailsInlineInput
  ) {
    createManualConsumption(
      areaIds: $areaIds
      buildingId: $buildingId
      consumptionTypeId: $consumptionTypeId
      cost: $cost
      displayUnitValue: $displayUnitValue
      energySourceTypeId: $energySourceTypeId
      from: $from
      provider: $provider
      sourceTypeId: $sourceTypeId
      subTypeId: $subTypeId
      to: $to
      value: $value
      wasteDetails: $wasteDetails
    ) {
      id
    }
  }
`);

// This should be deleted and replaced by the backend mutation. Right?
export const INSERT_SINGLE_CONSUMPTION_FROM_DRAFT = graphql(`
  mutation DataCollectionInsertConsumption(
    $consumptionDraftId: Int!
    $areaIds: [Int!]!
    $buildingId: Int!
    $from: DateTime!
    $to: DateTime!
    $value: Float
    $displayUnitValue: consumption_unit_enum
    $cost: Float
    $provider: String!
    $sourceTypeId: consumption_source_type_enum!
    $consumptionTypeId: consumption_type_enum!
    $energySourceTypeId: energy_source_type_enum
    $subTypeId: consumption_sub_type_enum
    $createdByUserId: Int!
  ) {
    createConsumptionFromDraft(
      consumptionDraftId: $consumptionDraftId
      areaIds: $areaIds
      buildingId: $buildingId
      from: $from
      to: $to
      value: $value
      displayUnitValue: $displayUnitValue
      cost: $cost
      provider: $provider
      sourceTypeId: $sourceTypeId
      consumptionTypeId: $consumptionTypeId
      energySourceTypeId: $energySourceTypeId
      subTypeId: $subTypeId
      createdByUserId: $createdByUserId
    ) {
      id
      consumption {
        id
        consumption_type_id
        cost
        created_by_user_id
        energy_source_type_id
        from
        provider
        source_type_id
        sub_type_id
        to
        value
        display_unit_value
      }
    }
  }
`);

export const INSERT_SINGLE_WASTE_DETAILS = graphql(`
  mutation DataCollectionInsertConsumptionWasteDetails(
    $id: Int!
    $amount: Float!
    $frequency: Float!
    $displayUnitFrequency: consumption_frequency_type_enum
    $price: Float!
    $volume: Float!
    $displayUnitVolume: consumption_unit_enum
  ) {
    createConsumptionWasteDetails(
      id: $id
      amount: $amount
      frequency: $frequency
      displayUnitFrequency: $displayUnitFrequency
      price: $price
      volume: $volume
      displayUnitVolume: $displayUnitVolume
    ) {
      id
      consumption_waste_details {
        id
        amount
        frequency
        display_unit_frequency
        price
        volume
        display_unit_volume
      }
    }
  }
`);

// This should be deleted and replaced by the backend mutation. Right?
export const UPDATE_SINGLE_CONSUMPTION = graphql(`
  mutation DataCollectionSaveConsumption(
    $consumptionId: Int!
    $areaIds: [Int!]!
    $from: DateTime!
    $to: DateTime!
    $value: Float
    $displayUnitValue: consumption_unit_enum
    $cost: Float
    $provider: String
    $consumptionTypeId: consumption_type_enum!
    $energySourceTypeId: energy_source_type_enum
    $subTypeId: consumption_sub_type_enum
  ) {
    updateConsumption(
      consumptionId: $consumptionId
      areaIds: $areaIds
      from: $from
      to: $to
      value: $value
      displayUnitValue: $displayUnitValue
      cost: $cost
      provider: $provider
      consumptionTypeId: $consumptionTypeId
      energySourceTypeId: $energySourceTypeId
      subTypeId: $subTypeId
    ) {
      id
      consumption {
        id
        consumption_type_id
        cost
        created_by_user_id
        energy_source_type_id
        from
        provider
        source_type_id
        sub_type_id
        to
        value
        display_unit_value
        consumption_waste_detail {
          id
        }
      }
    }
  }
`);

export const UPDATE_SINGLE_WASTE_DETAILS = graphql(`
  mutation DataCollectionUpdateConsumptionWasteDetails(
    $id: Int!
    $amount: Float!
    $frequency: Float!
    $displayUnitFrequency: consumption_frequency_type_enum
    $price: Float!
    $volume: Float!
    $displayUnitVolume: consumption_unit_enum
  ) {
    updateConsumptionWasteDetails(
      id: $id
      amount: $amount
      frequency: $frequency
      displayUnitFrequency: $displayUnitFrequency
      price: $price
      volume: $volume
      displayUnitVolume: $displayUnitVolume
    ) {
      id
      consumption_waste_details {
        id
        amount
        frequency
        display_unit_frequency
        price
        volume
        display_unit_volume
      }
    }
  }
`);

export const DELETE_SINGLE_WASTE_DETAILS = graphql(`
  mutation DataCollectionDeleteConsumptionWasteDetails($id: Int!) {
    delete_consumption_waste_details_by_pk(id: $id) {
      id
    }
  }
`);

/**
 * Delete a processed bill by PK
 */
export const DELETE_CONSUMPTION_INVOICE = graphql(`
  mutation DeleteConsumptionInvoice($id: Int!) {
    delete_consumption_invoice_by_pk(id: $id) {
      id
      __typename
    }
  }
`);

export const DELETE_CONSUMPTION = graphql(`
  mutation DeleteConsumption($id: Int!) {
    delete_consumption_by_pk(id: $id) {
      id
      __typename
    }
  }
`);

/**
 * Delete a processed bill draft by PK
 */
export const DELETE_CONSUMPTION_INVOICE_DRAFT = graphql(`
  mutation DeleteConsumptionInvoiceDraft($invoiceDraftId: Int!) {
    delete_consumption_invoice_draft_by_pk(id: $invoiceDraftId) {
      id
      __typename
      consumption_drafts {
        id
        __typename
      }
    }
  }
`);

export const DELETE_CONSUMPTION_DRAFT = graphql(`
  mutation DataCollectionAreaConsumptionDraftDeleteConsumptionDraft($consumptionDraftId: Int!) {
    delete_consumption_draft_by_pk(id: $consumptionDraftId) {
      __typename
      id
    }
  }
`);

// Custom emission certificates ------------------------------------

export const CREATE_EMISSION_CERTIFICATE_DRAFT_WITHOUT_FILE = graphql(`
  mutation CreateEmissionCertificateDraftWithoutFile(
    $emissionCertificateDraftWithoutFile: CreateEmissionCertificateDraftWithoutFileInput!
  ) {
    createEmissionCertificateDraftWithoutFile(
      emissionCertificateDraftWithoutFile: $emissionCertificateDraftWithoutFile
    ) {
      emission_certificate_draft_id
    }
  }
`);

export const CREATE_EMISSION_CERTIFICATE_DRAFT_WITH_FILE = graphql(`
  mutation CreateEmissionCertificateDraftWithFile(
    $emissionCertificateDraftWithoutFile: CreateEmissionCertificateDraftWithoutFileInput!
  ) {
    createEmissionCertificateDraftWithFile(emissionCertificateDraftWithoutFile: $emissionCertificateDraftWithoutFile) {
      fileUrl: upload_url
      fileId: file_id
    }
  }
`);

export const UPDATE_EMISSION_CERTIFICATE_DRAFT_WITHOUT_FILE = graphql(`
  mutation UpdateEmissionCertificateDraftWithoutFile(
    $emissionCertificateDraftWithoutFile: UpdateEmissionCertificateDraftWithoutFileInput!
  ) {
    updateEmissionCertificateDraftWithoutFile(
      emissionCertificateDraftWithoutFile: $emissionCertificateDraftWithoutFile
    ) {
      emission_certificate_draft_id
    }
  }
`);

export const UPDATE_EMISSION_CERTIFICATE_DRAFT_WITH_FILE = graphql(`
  mutation UpdateEmissionCertificateDraftWithFile(
    $emissionCertificateDraftWithoutFile: UpdateEmissionCertificateDraftWithoutFileInput!
  ) {
    updateEmissionCertificateDraftWithFile(emissionCertificateDraftWithoutFile: $emissionCertificateDraftWithoutFile) {
      fileUrl: upload_url
      fileId: file_id
    }
  }
`);

export const CREATE_EMISSION_CERTIFICATE = graphql(`
  mutation CreateEmissionCertificate($emissionCertificate: CreateEmissionCertificateInput!) {
    createEmissionCertificate(emissionCertificate: $emissionCertificate) {
      emission_certificate_id
    }
  }
`);

export const UPDATE_EMISSION_CERTIFICATE_WITHOUT_FILE = graphql(`
  mutation UpdateEmissionCertificateWithoutFile($emissionCertificate: UpdateEmissionCertificateWithoutFileInput!) {
    updateEmissionCertificateWithoutFile(emissionCertificate: $emissionCertificate) {
      success
    }
  }
`);

export const UPDATE_EMISSION_CERTIFICATE_FILE = graphql(`
  mutation UpdateEmissionCertificateFile($emissionCertificateId: Int!) {
    updateEmissionCertificateFile(emission_certificate_id: $emissionCertificateId) {
      fileUrl: upload_url
      fileId: file_id
    }
  }
`);

export const DELETE_EMISSION_CERTIFICATE = graphql(`
  mutation DeleteEmissionCertificate($id: Int!) {
    deleteEmissionCertificate(id: $id) {
      success
    }
  }
`);

export const DELETE_EMISSION_CERTIFICATE_DRAFT = graphql(`
  mutation DeleteEmissionCertificateDraft($id: Int!) {
    delete_emission_certificate_draft_by_pk(id: $id) {
      __typename
      id
    }
  }
`);

export const EDIT_AREAS = graphql(`
  mutation EditAreas($buildingId: Int!, $areas: [AreaUpdateInput!]!, $saveInvalidState: Boolean!) {
    editAreas(buildingId: $buildingId, areas: $areas, saveInvalidState: $saveInvalidState) {
      success
      scalingResult {
        final_energy
        final_energy_scaled
        scaling_successful
      }
      efficiencyClassDeviation {
        previousEfficiencyClass
        newEfficiencyClass
      }
    }
  }
`);

export const PREVIEW_EDIT_AREAS = graphql(`
  mutation PreviewEditAreas($buildingId: Int!, $areas: [AreaUpdateInput!]!, $saveInvalidState: Boolean!) {
    previewEditAreas(buildingId: $buildingId, areas: $areas, saveInvalidState: $saveInvalidState) {
      success
      efficiencyClassDeviation {
        previousEfficiencyClass
        newEfficiencyClass
      }
    }
  }
`);

export const MOVE_BUILDINGS_TO_ECONOMIC_UNIT = graphql(`
  mutation MoveBuildingsToEconomicUnit(
    $destinationEconomicUnitId: Int!
    $keepEmptyEconomicUnits: Boolean!
    $buildingFilter: BuildingFilter!
  ) {
    moveAllBuildings(
      destinationEconomicUnitId: $destinationEconomicUnitId
      keepEmptyEconomicUnits: $keepEmptyEconomicUnits
      filter: $buildingFilter
    ) {
      success
    }
  }
`);

export const EXPORT_FILE = graphql(`
  mutation ExportFile($input: ExportFileInput!) {
    exportFile(input: $input) {
      fileName
      jobId
      status
    }
  }
`);

export const DELETE_BUILDINGS = graphql(`
  mutation DeleteBuildings($buildingFilter: BuildingFilter!, $keepEmptyEconomicUnits: Boolean!) {
    deleteAllBuildings(filter: $buildingFilter, keepEmptyEconomicUnits: $keepEmptyEconomicUnits) {
      success
    }
  }
`);

export const EDIT_BUILDING_DATA = graphql(`
  mutation EditBuildingData(
    $buildingId: Int!
    $buildingData: building_set_input!
    $addressId: Int!
    $addressData: address_set_input!
    $valuations: [building_valuation_insert_input!]!
    $deletedValuationIds: [Int!]!
    $shouldDeleteDistrictHeating: Boolean!
    $shouldUpsertDistrictHeating: Boolean!
    $districtHeatingYear: Int
    $insertCertificates: [CreateCertificate!]!
    $updateCertificates: [EditCertificate!]!
    $deleteCertificates: [DeleteCertificate!]!
  ) {
    update_building_by_pk(pk_columns: { id: $buildingId }, _set: $buildingData) {
      id
      building_state_id
      monument_protection
      leasehold
      heritage_district
      milieu_protection
      customer_external_identifier
      description
      units
      units_data_source
    }
    update_address_by_pk(pk_columns: { id: $addressId }, _set: $addressData) {
      id
      street
      postal_code
      city
    }

    insert_building_valuation(
      objects: $valuations
      on_conflict: { constraint: building_valuation_pkey, update_columns: [valuation_at, value] }
    ) {
      returning {
        id
        valuation_at
        value
        building_id
      }
    }

    delete_building_valuation(where: { id: { _in: $deletedValuationIds } }) {
      affected_rows
    }

    delete_building_district_heating_availability(where: { building_id: { _eq: $buildingId } })
      @include(if: $shouldDeleteDistrictHeating) {
      affected_rows
    }

    insert_building_district_heating_availability_one(
      object: { building_id: $buildingId, availability_year: $districtHeatingYear }
      on_conflict: { constraint: building_district_heating_availability_pkey, update_columns: [availability_year] }
    ) @include(if: $shouldUpsertDistrictHeating) {
      id: building_id
      building {
        id
        district_heating_availability_status {
          id: building_id
          district_heating_status
          district_heating_year
          updated_at
        }
      }
    }

    editCertificates(
      buildingId: $buildingId
      deleteCertificates: $deleteCertificates
      insertCertificates: $insertCertificates
      updateCertificates: $updateCertificates
    ) {
      certificates {
        id
      }
    }
  }
`);

export const UPDATE_BUILDING_CUSTOM_DATA = graphql(`
  mutation UpdateBuildingCustomData($buildingId: Int!, $customData: JSON!) {
    updateBuildingCustomData(buildingId: $buildingId, customData: $customData) {
      success
      errorMessage
    }
  }
`);

export const CREATE_BUILDING_DRAFT = graphql(`
  mutation CreateBuildingDraft($buildingDraft: building_draft_insert_input!) {
    insert_building_draft_one(object: $buildingDraft) {
      ...GetBuildingDraftFragment
    }
  }
`);

export const UPDATE_BUILDING_DRAFT = graphql(`
  mutation UpdateBuildingDraft(
    $id: Int!
    $buildingDraft: building_draft_set_input! = {}
    $valuationInserts: [building_draft_valuation_insert_input!]! = []
    $valuationUpdates: [building_draft_valuation_updates!]! = []
    $valuationDeletedIds: [Int!] = []
    $consumerInserts: [building_draft_consumer_insert_input!]! = []
    $consumerUpdates: [building_draft_consumer_updates!]! = []
    $consumerDeletedIds: [Int!]! = []
    $areaInserts: [building_draft_area_insert_input!]! = []
    $areaUpdates: [building_draft_area_updates!]! = []
    $areaDeletedIds: [Int!]! = []
    $deleteCertificates: [DeleteCertificate!]! = []
    $insertCertificates: [CreateCertificate!]! = []
    $updateCertificates: [EditCertificate!]! = []
  ) {
    update_building_draft_by_pk(pk_columns: { id: $id }, _set: $buildingDraft) {
      id
    }
    insert_building_draft_valuation(objects: $valuationInserts) {
      affected_rows
    }
    update_building_draft_valuation_many(updates: $valuationUpdates) {
      affected_rows
    }
    delete_building_draft_valuation(where: { id: { _in: $valuationDeletedIds } }) {
      affected_rows
    }
    insert_building_draft_consumer(objects: $consumerInserts) {
      affected_rows
    }
    update_building_draft_consumer_many(updates: $consumerUpdates) {
      affected_rows
    }
    delete_building_draft_consumer(where: { id: { _in: $consumerDeletedIds } }) {
      affected_rows
    }
    insert_building_draft_area(objects: $areaInserts) {
      affected_rows
    }
    update_building_draft_area_many(updates: $areaUpdates) {
      affected_rows
    }
    delete_building_draft_area(where: { id: { _in: $areaDeletedIds } }) {
      affected_rows
    }
    update_building_draft_consumer(
      where: { building_draft_area_id: { _in: $areaDeletedIds } }
      _set: { building_draft_area_id: null }
    ) {
      affected_rows
    }
    editCertificates(
      buildingDraftId: $id
      deleteCertificates: $deleteCertificates
      insertCertificates: $insertCertificates
      updateCertificates: $updateCertificates
    ) {
      certificates {
        id
      }
    }
  }
`);

export const CREATE_BUILDING_FROM_BUILDING_DRAFT = graphql(`
  mutation CreateBuildingFromBuildingDraft($buildingDraftId: Int!) {
    createBuildingFromBuildingDraft(buildingDraftId: $buildingDraftId) {
      buildingId
      success
    }
  }
`);

export const EXPORT_CLIMATE_RISK_V2_REPORT = graphql(`
  mutation ExportClimateRiskV2Report($buildingId: Int!) {
    exportClimateRiskV2Report(buildingId: $buildingId) {
      body
    }
  }
`);

export const UPDATE_ENERGY_CERTIFICATE_DRAFT = graphql(`
  mutation UpdateEnergyCertificateDraft(
    $obj: energy_certificate_draft_set_input!
    $id: Int!
    $insertConsumers: [energy_certificate_consumer_insert_input!]!
    $updateConsumers: [energy_certificate_consumer_updates!]!
    $deleteConsumers: [Int!]!
    $insertAreas: [energy_certificate_draft_area_insert_input!]!
    $updateAreas: [energy_certificate_draft_area_updates!]!
    $deleteAreas: [Int!]!
  ) {
    update_energy_certificate_draft_by_pk(pk_columns: { id: $id }, _set: $obj) {
      id
    }
    insert_energy_certificate_consumer(objects: $insertConsumers) {
      affected_rows
    }
    update_energy_certificate_consumer_many(updates: $updateConsumers) {
      affected_rows
    }
    delete_energy_certificate_consumer(where: { id: { _in: $deleteConsumers } }) {
      affected_rows
    }
    insert_energy_certificate_draft_area(objects: $insertAreas) {
      affected_rows
    }
    update_energy_certificate_draft_area_many(updates: $updateAreas) {
      affected_rows
    }
    delete_energy_certificate_draft_area(where: { id: { _in: $deleteAreas } }) {
      affected_rows
    }
  }
`);

export const CREATE_EPC_FROM_DRAFT = graphql(`
  mutation CreateEPCFromDraft($energyCertificateDraftId: Int!) {
    transformEnergyCertificateDraftToEnergyCertificate(energyCertificateDraftId: $energyCertificateDraftId) {
      success
      energyCertificateId
      EnergyCertificateDraftTransformOutput {
        ...EPCFragment
      }
    }
  }
`);

export const FILL_BUILDING_DRAFT = graphql(`
  mutation FillBuildingDraft($buildingDraftId: Int!) {
    fillBuildingDraft(buildingDraftId: $buildingDraftId) {
      success
      BuildingDraftId {
        ...GetBuildingDraftFragment
      }
    }
  }
`);

export const RESET_BUILDING_DRAFT = graphql(`
  mutation ResetBuildingDraft($buildingDraftId: Int!) {
    resetBuildingDraft(buildingDraftId: $buildingDraftId) {
      success
      buildingDraft {
        ...GetBuildingDraftFragment
      }
    }
  }
`);

export const CREATE_BUILDING_MODEL_TWIN = graphql(`
  mutation CreateBuildingModelTwin($buildingId: Int!) {
    createBuildingModelTwin(buildingId: $buildingId) {
      success
      errorMessage
      buildingModelId
    }
  }
`);

export const SET_ACTIVE_BUILDING_MODEL = graphql(`
  mutation SetActiveBuildingModel($buildingId: Int!, $buildingModelId: Int!) {
    setActiveBuildingModel(buildingId: $buildingId, buildingModelId: $buildingModelId) {
      success
    }
  }
`);

export const DELETE_BUILDING_MODEL_DRAFT = graphql(`
  mutation DeleteBuildingModelDraft($buildingId: Int!, $buildingModelId: Int!) {
    deleteTwinBuildingModel(buildingId: $buildingId, buildingModelId: $buildingModelId) {
      success
    }
  }
`);

export const CREATE_CERTIFICATE_FILE_UPLOAD_URL = graphql(`
  mutation CreateCertificateFileUploadURL($buildingId: Int, $buildingDraftId: Int, $fileName: String!) {
    createCertificateFileUploadURL(buildingId: $buildingId, buildingDraftId: $buildingDraftId, fileName: $fileName) {
      fileId
      uploadURL
    }
  }
`);

export const SET_BUILDING_INACTIVE = graphql(`
  mutation SetBuildingInactive($id: Int!) {
    update_building_by_pk(pk_columns: { id: $id }, _set: { state: INACTIVE }) {
      id
      state
      economic_unit {
        id
        associatedBuildingsAndDrafts {
          amount
        }
      }
    }
  }
`);

export const SET_BUILDING_ACTIVE = graphql(`
  mutation SetBuildingActive($id: Int!) {
    update_inactive_buildings(where: { id: { _eq: $id } }, _set: { state: "ACTIVE" }) {
      returning {
        id
        state
        economic_unit {
          id
          associatedBuildingsAndDrafts {
            amount
          }
        }
      }
    }
  }
`);

export const UPDATE_BUILDING_MODEL_TWIN = graphql(`
  mutation UpdateBuildingModelTwin($buildingModelId: Int!, $description: String!, $validFrom: DateTime!) {
    updateBuildingModelTwin(buildingModelId: $buildingModelId, description: $description, validFrom: $validFrom) {
      buildingModelId
    }
  }
`);

export const DUPLICATE_BUILDING_MODEL_VERSION_TWIN = graphql(`
  mutation DuplicateBuildingModelVersionTwin($buildingId: Int!, $description: String!, $validFrom: DateTime!) {
    duplicateBuildingModelVersionTwin(buildingId: $buildingId, description: $description, validFrom: $validFrom) {
      buildingModelId
    }
  }
`);

export const DELETE_BUILDING_MODEL_TWIN = graphql(`
  mutation DeleteBuildingModelTwin($buildingModelId: Int!) {
    deleteBuildingModelTwin(buildingModelId: $buildingModelId) {
      buildingModelId
      newBuildingModelId
    }
  }
`);
